<template>
  <div
    v-if="health.cards.length"
    class="grid grid-cols-1 gap-4 lg:col-span-2 lg:grid-cols-2"
    data-test-id="program-health"
  >
    <Card
      v-for="card in health.cards"
      :key="card.id"
      class="min-w-0"
      :class="card.steps.length ? 'lg:col-span-2' : ''"
      :data-test-id="`program-health-${card.id}`"
    >
      <CardHeader>
        <div class="flex flex-wrap items-center gap-1">
          <CardTitle class="text-base">{{ card.title }}</CardTitle>
          <InfoHint v-if="card.hint" :label="card.title" :text="card.hint" />
          <HealthLevel v-if="card.measure?.level" :level="card.measure.level" class="ml-auto" />
        </div>
        <CardDescription v-if="card.description">{{ card.description }}</CardDescription>
      </CardHeader>
      <CardContent>
        <div class="flex flex-wrap gap-8">
          <div class="flex min-w-0 flex-[2_1_20rem] flex-col gap-3">
            <template v-if="card.measure && card.measure.d > 0">
              <p class="flex flex-wrap items-baseline gap-2">
                <span class="text-3xl font-semibold tabular-nums">{{ outOf(card.measure) }}</span>
                <span class="text-sm text-muted-foreground">{{ card.unit }}</span>
              </p>
              <p v-if="card.excluded" class="text-xs text-muted-foreground">{{ card.excluded }}</p>
              <div class="relative h-3 rounded-full bg-muted" aria-hidden="true">
                <div
                  class="absolute inset-y-0 left-0 rounded-full"
                  :class="barFill(card.measure, health.levels)"
                  :style="{ width: `${shareOf(card.measure)}%` }"
                />
              </div>
              <p v-if="card.measure.d < MIN_UNITS" class="text-xs text-muted-foreground">
                Moins de 10 {{ card.noun }} : à lire avec prudence.
              </p>
            </template>
            <p v-else-if="card.measure" class="text-xl font-semibold text-muted-foreground">{{ card.empty }}</p>
            <p v-if="card.explanation" class="text-sm leading-relaxed text-muted-foreground">{{ card.explanation }}</p>
            <ul v-if="card.causes.length" class="flex flex-col">
              <li
                v-for="cause in card.causes"
                :key="cause.text"
                class="flex flex-wrap items-center gap-x-3 gap-y-1 border-t py-2"
              >
                <span class="w-10 text-sm font-semibold tabular-nums">{{ cause.n.toLocaleString('fr-FR') }}</span>
                <span class="min-w-0 flex-[1_1_12rem] text-sm">{{ cause.text }}</span>
                <Button
                  v-if="cause.list"
                  variant="ghost"
                  size="sm"
                  :aria-label="`Voir la liste : ${cleanTitle(cause.list.title)}`"
                  @click="emit('openList', cause.list.key)"
                >
                  Voir la liste
                </Button>
              </li>
            </ul>
          </div>
          <!-- The steps of the journey: each one is read on its own population and its own age. -->
          <dl
            v-if="card.steps.length"
            class="grid min-w-0 flex-[3_1_24rem] grid-cols-[repeat(auto-fit,minmax(9.5rem,1fr))] gap-4"
          >
            <div v-for="step in card.steps" :key="step.label" class="flex flex-col gap-1 border-l pl-4">
              <dt class="text-xs text-muted-foreground">{{ step.label }}</dt>
              <template v-if="step.measure.d > 0">
                <dd class="text-xl font-semibold tabular-nums">{{ outOf(step.measure) }}</dd>
                <dd v-if="step.measure.level"><HealthLevel :level="step.measure.level" /></dd>
                <dd class="text-[13px] leading-snug text-muted-foreground">{{ step.text }}</dd>
                <dd v-if="step.excluded" class="text-xs leading-snug text-muted-foreground">{{ step.excluded }}</dd>
              </template>
              <template v-else>
                <dd class="text-base font-semibold text-muted-foreground">{{ step.empty }}</dd>
                <dd class="text-[13px] leading-snug text-muted-foreground">{{ step.text }}</dd>
              </template>
            </div>
          </dl>
        </div>
      </CardContent>
    </Card>
  </div>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import HealthLevel from '@/features/ui/pages/admin/dashboard/dashboard-content/health-level.vue'
import InfoHint from '@/features/ui/pages/admin/dashboard/dashboard-content/info-hint.vue'
import {
  barFill,
  MIN_UNITS,
  outOf,
  type ProgramHealthType,
  shareOf,
} from '@/features/ui/pages/admin/dashboard/dashboard-content/program-health'

// The cards of the program objective, in place of the sante_* charts. « Voir la liste » opens the list in « À relancer ».
defineProps<{ health: ProgramHealthType }>()
const emit = defineEmits<{ openList: [key: string] }>()

const cleanTitle = (title: string) => title.replace(/^MDIV - /, '')
</script>
